import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
//import emailjs from 'emailjs-com';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Contact = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    projectType: '',
    otherProjectType: ''
  });

  // Form validation state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  
  // Form submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [formValid, setFormValid] = useState(false);
  
  // Check if all required fields are valid whenever formData or errors change
  useEffect(() => {
    const validateFormData = () => {
      // Check if all required fields have values and no errors exist
      const requiredFields = ['name', 'email', 'phone', 'projectType', 'message'];
      const hasAllRequiredFields = requiredFields.every(field => {
        if (field === 'projectType' && formData.projectType === 'other') {
          return formData.otherProjectType.trim() !== '';
        }
        return formData[field].trim() !== '';
      });
      
      const hasNoErrors = Object.keys(errors).length === 0;
      
      setFormValid(hasAllRequiredFields && hasNoErrors);
    };
    
    validateFormData();
  }, [formData, errors]);

  // Format phone number as user types
  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format the phone number as XXX-XXX-XXXX
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
    } else {
      return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      // Format phone number
      const formattedPhone = formatPhoneNumber(value);
      setFormData(prevState => ({
        ...prevState,
        [name]: formattedPhone
      }));
    } else {
      setFormData(prevState => ({
        ...prevState,
        [name]: value
      }));
    }
    
    // Mark field as touched
    if (!touched[name]) {
      setTouched(prev => ({
        ...prev,
        [name]: true
      }));
    }
    
    // Validate field on change
    validateField(name, name === 'phone' ? formatPhoneNumber(value) : value);
  };

  // Handle field blur for validation
  const handleBlur = (e) => {
    const { name } = e.target;
    
    // Mark field as touched
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    
    // Validate the field
    validateField(name, formData[name]);
  };

  // Validate individual field
  const validateField = (name, value) => {
    let errorMessage = '';
    
    switch (name) {
      case 'name':
        if (!value.trim()) {
          errorMessage = 'Full name is required';
        }
        break;
      
      case 'email':
        if (!value.trim()) {
          errorMessage = 'Email address is required';
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
          errorMessage = 'Invalid email address';
        }
        break;
      
      case 'phone':
        if (!value.trim()) {
          errorMessage = 'Phone number is required';
        } else if (value.replace(/\D/g, '').length !== 10) {
          errorMessage = 'Phone number must be 10 digits';
        }
        break;
      
      case 'projectType':
        if (!value) {
          errorMessage = 'Please select a project type';
        }
        break;
      
      case 'otherProjectType':
        if (formData.projectType === 'other' && !value.trim()) {
          errorMessage = 'Please specify your project type';
        }
        break;
      
      case 'message':
        if (!value.trim()) {
          errorMessage = 'Message is required';
        }
        break;
      
      default:
        break;
    }
    
    // Update errors state
    setErrors(prev => {
      if (errorMessage) {
        return { ...prev, [name]: errorMessage };
      } else {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      }
    });
    
    return !errorMessage;
  };

  // Validate entire form
  const validateForm = () => {
    const fieldsToValidate = [
      'name', 
      'email', 
      'phone', 
      'projectType', 
      'message'
    ];
    
    if (formData.projectType === 'other') {
      fieldsToValidate.push('otherProjectType');
    }
    
    const validationResults = {};
    let isValid = true;
    
    fieldsToValidate.forEach(field => {
      const fieldIsValid = validateField(field, formData[field]);
      if (!fieldIsValid) {
        isValid = false;
        validationResults[field] = errors[field] || `${field} is invalid`;
      }
    });
    
    // Mark all fields as touched
    const allTouched = fieldsToValidate.reduce((acc, field) => {
      acc[field] = true;
      return acc;
    }, {});
    
    setTouched(prev => ({ ...prev, ...allTouched }));
    setErrors(validationResults);
    
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      
      /* Prepare email data
      const emailData = {
        to_email: 'info@jandcrenovations.com',
        from_name: formData.name,
        from_email: formData.email,
        from_phone: formData.phone,
        project_type: formData.projectType === 'other' ? formData.otherProjectType : formData.projectType,
        message: formData.message
      };
      */
      
      try {
        // Send email using EmailJS
        // Note: You'll need to replace these IDs with your actual EmailJS service, template, and user IDs
        /*await emailjs.send(
          'service_id', // Replace with your EmailJS service ID
          'template_id', // Replace with your EmailJS template ID
          emailData,
          'user_id' // Replace with your EmailJS user ID
        );
        */
        
        // Show success toast
        toast.success('Your message has been sent successfully!', {
          position: "bottom-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        
        setSubmitSuccess(true);
        
        // Reset form after successful submission
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          projectType: '',
          otherProjectType: ''
        });
        
        setTouched({});
        
      } catch (error) {
        console.error('Error sending email:', error);
        
        // Show error toast
        toast.error('Failed to send message. Application is in development.', {
          position: "bottom-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <section id="contact" className="contact-section">
      <div className="container">
        <div className="section-title centered">
          <h2>Contact Us</h2>
          <p>Get in Touch for Your Next Project</p>
        </div>

        <motion.div 
          className="contact-container"
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.div className="contact-info" variants={itemVariants}>
            <div className="info-card">
              <h3>Contact Information</h3>
              <p>Reach out to discuss your project requirements or schedule a consultation.</p>
              
              <ul className="contact-details">
                <li>
                  <span className="icon">📍</span>
                  <div>
                    <strong>Location</strong>
                    <p>Waddy, KY 40076</p>
                  </div>
                </li>
                <li>
                  <span className="icon">📞</span>
                  <div>
                    <strong>Phone</strong>
                    <p>(502) 220-7640</p>
                  </div>
                </li>
                <li>
                  <span className="icon">✉️</span>
                  <div>
                    <strong>Email</strong>
                    <p>info@jandcrenovations.com</p>
                  </div>
                </li>
                <li>
                  <span className="icon">🕒</span>
                  <div>
                    <strong>Hours</strong>
                    <p>Monday - Friday: 8:00 AM - 5:00 PM</p>
                  </div>
                </li>
              </ul>
              
              <div className="government-note">
                <h4>Government Contracting</h4>
                <p>
                  For government contracting inquiries, please mention your agency and project 
                  requirements in your message. We're fully prepared to meet federal, state, 
                  and local government contracting standards.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div className="contact-form-container" variants={itemVariants}>
            {submitSuccess ? (
              <div className="form-success">
                <div className="success-icon">✓</div>
                <h3>Thank You!</h3>
                <p>Your message has been sent successfully. We'll get back to you shortly.</p>
              </div>
            ) : (
              <form className="contact-form" onSubmit={handleSubmit} noValidate>
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={touched.name && errors.name ? 'error' : ''}
                    required
                  />
                  {touched.name && errors.name && <span className="error-message">{errors.name}</span>}
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={touched.email && errors.email ? 'error' : ''}
                      required
                    />
                    {touched.email && errors.email && <span className="error-message">{errors.email}</span>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="123-456-7890"
                      className={touched.phone && errors.phone ? 'error' : ''}
                      required
                    />
                    {touched.phone && errors.phone && <span className="error-message">{errors.phone}</span>}
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="projectType">Project Type *</label>
                  <select
                    id="projectType"
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={touched.projectType && errors.projectType ? 'error' : ''}
                    required
                  >
                    <option value="">Select Project Type</option>
                    <option value="commercial">Commercial Construction</option>
                    <option value="residential">Residential Construction</option>
                    <option value="government">Government Project</option>
                    <option value="renovation">Renovation</option>
                    <option value="infrastructure">Infrastructure</option>
                    <option value="other">Other</option>
                  </select>
                  {touched.projectType && errors.projectType && <span className="error-message">{errors.projectType}</span>}
                </div>
                
                {formData.projectType === 'other' && (
                  <div className="form-group">
                    <label htmlFor="otherProjectType">Specify Project Type *</label>
                    <input
                      type="text"
                      id="otherProjectType"
                      name="otherProjectType"
                      value={formData.otherProjectType}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={touched.otherProjectType && errors.otherProjectType ? 'error' : ''}
                      required
                    />
                    {touched.otherProjectType && errors.otherProjectType && <span className="error-message">{errors.otherProjectType}</span>}
                  </div>
                )}
                
                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={touched.message && errors.message ? 'error' : ''}
                    required
                  ></textarea>
                  {touched.message && errors.message && <span className="error-message">{errors.message}</span>}
                </div>
                
                <div className="form-submit">
                  <button 
                    type="submit" 
                    className="btn btn-primary btn-lg"
                    disabled={isSubmitting || !formValid}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </motion.div>
      </div>
      
      {/* Toast notification container */}
      <ToastContainer />
    </section>
  );
};

export default Contact;
